import React from 'react';

import PropTypes from 'prop-types';

import clsx from 'clsx';

import { useDevice } from '@bonnet/next/device';

import { Carousel, ResponsiveImage } from 'reaxl';
import { EmailCTA } from 'reaxl-email';

import MediaGalleryImage from './MediaGalleryImage';

function MediaSlider({
    emailCTAProps,
    images,
    listingTitle = 'Listing',
    onImageClick,
    onNextButtonClick,
    onPrevButtonClick,
}) {
    const device = useDevice();

    const isSm = device.is.sm;
    const isMd = device.is.md;
    const isLg = device.is.lg;

    let carouselProps = {
        fullWidthScroll: true,
        initialIndex: 0,
        onNextButtonClick,
        onPrevButtonClick,
        scrollItemsCount: 1,
        visibilityThreshold: 0.9,
    };

    const fallbackImg = '/cm-api/content/static/img/placeholders/vin_placeholder.png';

    // simple slider for small screens
    if (!(isMd || isLg)) {
        carouselProps = {
            ...carouselProps,
            className: isSm ? 'img-rounded' : 'row', // border-radius or extend to browser edge
            fullWidthScrollXs: true, // enable touch/drag events
            prevNextButtons: false,
            singleImageMode: true,
        };

        return (
            <Carousel
                {...carouselProps}
            >
                {images.map((image, index) => (
                    <MediaGalleryImage
                        alt={`${listingTitle} image ${index + 1}`}
                        isSlider
                        key={image.src}
                        lazyImage={index < 2 ? 'eager' : 'lazy'}
                        onClick={(e) => onImageClick(e, index)}
                        src={image.src}
                        type="main"
                    />
                ))}
            </Carousel>
        );
    }

    const [firstImage, ...restImages] = images || [];

    const columns = [];
    for (let i = 0; i < restImages.length; i += 2) {
        columns.push(restImages.slice(i, i + 2));
    }

    const renderRequestPhoto = () => (
        <div
            key="RequestPhotoButton"
            className="padding-1"
        >
            <EmailCTA
                {...emailCTAProps}
                className="request-photo-button img-rounded"
                showIcon={false}
                buttonStyle="link"
                context="mediaGallery"
                type="button"
                labelKey="morePhotos"
            />
        </div>
    );

    const renderImgPlaceholder = () => (
        <div
            style={{ position: 'relative' }}
            key="ImgPlaceholder"
            className="padding-1"
        >
            <ResponsiveImage
                alt={`${listingTitle} image placeholder`}
                src={fallbackImg}
                lazyload={false}
            />
            <div className="placeholder-box-shadow img-rounded" />
        </div>
    );

    return (
        <Carousel
            {...carouselProps}
        >
            {firstImage && (
                <div
                    key="First-Image"
                    className={clsx('margin-1 media-slider-first-image', { md: isMd, lg: isLg })}
                >
                    <MediaGalleryImage
                        alt={`${listingTitle} image 1`}
                        isSlider
                        key={firstImage.src}
                        lazyImage="eager"
                        onClick={(e) => onImageClick(e, 0)}
                        src={firstImage.src}
                        type="main"
                    />
                </div>
            )}
            {columns.length === 0 ? (
                <div
                    key="ImgPlaceholderAndButton"
                    className={clsx('margin-left-1 media-slider-column', { md: isMd, lg: isLg })}
                >
                    {renderImgPlaceholder()}
                    {renderRequestPhoto()}
                </div>
            ) : columns.map((pair, colIdx) => (
                <div
                    key={'Column ' + colIdx}
                    className={clsx('margin-left-1 media-slider-column', { md: isMd, lg: isLg })}
                >
                    {pair.map((image, idx) => ((
                        <div
                            key={`Column-${colIdx}-Image-${idx}`}
                            className="padding-1"
                        >
                            <MediaGalleryImage
                                alt={`${listingTitle} image ${colIdx * 2 + idx + 2}`}
                                isSlider
                                key={image.src}
                                lazyImage={colIdx === 0 ? 'eager' : 'lazy'}
                                onClick={(e) => onImageClick(e, colIdx * 2 + idx + 1)}
                                src={image.src}
                                type="main"
                            />
                        </div>
                    )))}
                    {(columns.length === 1 && pair.length === 1) && renderRequestPhoto()}
                </div>
            ))}
        </Carousel>
    );
}

MediaSlider.propTypes = {
    images: PropTypes.arrayOf(
        PropTypes.shape({
            src: PropTypes.string.isRequired,
        })
    ).isRequired,
    isSm: PropTypes.bool,
    listingTitle: PropTypes.string,
    onImageClick: PropTypes.func.isRequired,
    onNextButtonClick: PropTypes.func,
    onPrevButtonClick: PropTypes.func,
};

export default React.memo(MediaSlider);
